import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useToast } from '@/hooks/useToast';
import { useI18n } from '@/hooks/useI18n';
import type { TranslationKey } from '@/types/i18n.types';
import {
    fetchPaymentsDashboard,
    fetchPaymentsFilterOptions,
    paymentsDashboardErrorKey,
} from '@/services/payments-dashboard.service';
import {
    markInstallmentPaid,
    paymentPlanErrorKey,
    sendInstallmentReminder,
} from '@/services/payment-plan.service';
import { PAYMENT_PLAN_QUERY_KEY } from '@/lib/builder/hooks/usePaymentPlan';
import {
    IPaymentsDashboard,
    PaymentsDashboardParams,
    PAYMENTS_DASHBOARD_QUERY_KEY,
} from '@/lib/payments/models/payments-dashboard.model';
import {
    IPaymentsFilterOptions,
    PAYMENTS_FILTER_OPTIONS_QUERY_KEY,
} from '@/lib/payments/models/payments-filters.model';
import { formatPlanAmount } from '@/lib/builder/utils/payment-format';
import type { AppLanguage } from '@/constants/languages';
import { LIST_MIN_LOADING_MS, withMinDuration } from '@/hooks/useServerData';

export { PAYMENTS_DASHBOARD_QUERY_KEY, PAYMENTS_FILTER_OPTIONS_QUERY_KEY };

export const paymentsDashboardQueryKey = (params: PaymentsDashboardParams) => [
    PAYMENTS_DASHBOARD_QUERY_KEY,
    params.dueWindowDays,
    params.designerId,
    params.itineraryId,
    params.clientKey,
    params.recentPage,
    params.duePage,
    params.itinerariesPage,
    params.unconfirmedPage,
    params.pageSize,
    params.itinerariesPageSize,
];

/**
 * Dashboard pagamenti. I parametri entrano nella query key: cambiare finestra o
 * collaboratore e' una fetch diversa, non un filtro applicato al risultato in cache.
 *
 * `placeholderData` tiene a schermo il risultato precedente mentre arriva il nuovo:
 * senza, ogni carattere digitato nella ricerca farebbe sparire la tabella.
 *
 * La risposta arriva dopo almeno LIST_MIN_LOADING_MS: le liste mostrano lo skeleton per
 * tutto il fetch, e senza un minimo lampeggerebbe per un frame prima delle righe.
 */
export const usePaymentsDashboardQuery = (params: PaymentsDashboardParams) =>
    useQuery<IPaymentsDashboard>({
        queryKey: paymentsDashboardQueryKey(params),
        queryFn: () => withMinDuration(fetchPaymentsDashboard(params), LIST_MIN_LOADING_MS),
        placeholderData: (previous) => previous,
        staleTime: 30_000,
    });

/**
 * Valori selezionabili nelle tendine dei filtri.
 *
 * Una sola chiamata all'apertura della pagina: finche' la lista non e' troncata, `query`
 * resta null e la stessa entry di cache serve qualunque cosa si stia digitando, perche' il
 * filtro avviene in memoria.
 *
 * `staleTime` a 10 minuti perche' l'insieme cambia solo quando qualcuno crea o cancella un
 * piano: le mutation del piano invalidano gia' questa chiave, quindi i 10 minuti coprono
 * solo il caso del collega che lavora in parallelo. Per quello c'e' il refresh manuale.
 */
export const usePaymentsFilterOptions = (params: {
    query: string | null;
    includeIds: string[];
    clientKeys: string[];
}) =>
    useQuery<IPaymentsFilterOptions>({
        queryKey: [
            PAYMENTS_FILTER_OPTIONS_QUERY_KEY,
            params.query,
            // Ordinati: due selezioni uguali in ordine diverso sono la stessa richiesta e
            // non devono produrre due entry di cache.
            [...params.includeIds].sort().join(','),
            [...params.clientKeys].sort().join(','),
        ],
        queryFn: () => fetchPaymentsFilterOptions(params),
        placeholderData: (previous) => previous,
        staleTime: 10 * 60_000,
    });

/**
 * "Segna come pagata" dalla dashboard. Stessa RPC della card nel builder
 * (mark_installment_paid, che autorizza con can_manage_itinerary), ma qui va invalidata
 * anche la query del piano del singolo itinerario: se il TD apre subito dopo il builder
 * troverebbe la rata ancora da incassare.
 */
export const useMarkInstallmentPaidFromDashboard = () => {
    const queryClient = useQueryClient();
    const { toast } = useToast();
    const { t } = useI18n();

    return useMutation({
        mutationFn: (params: {
            installmentId: string;
            itineraryId: string;
            paidAmount?: number;
            paymentMethod?: string;
            notifyClient?: boolean;
            language?: AppLanguage;
        }) => markInstallmentPaid(params),
        onSuccess: (data, variables) => {
            queryClient.invalidateQueries({ queryKey: [PAYMENTS_DASHBOARD_QUERY_KEY] });
            queryClient.invalidateQueries({
                queryKey: [PAYMENT_PLAN_QUERY_KEY, variables.itineraryId],
            });
            // Il debito si ripete qui, a operazione fatta: nella modale era una
            // previsione, questo e' l'importo che e' stato scritto davvero.
            toast({
                title: t('checkout.paymentPlan.markedPaid'),
                description: data.platformFeeAmount
                    ? t('payments.platformFee.toast', {
                          amount: formatPlanAmount(data.platformFeeAmount),
                      })
                    : undefined,
            });
        },
        onError: (error) => {
            toast({ title: t(paymentPlanErrorKey(error) as TranslationKey), variant: 'destructive' });
        },
    });
};

/**
 * Sollecito manuale su una rata.
 *
 * Non invalida la dashboard: l'invio non cambia importi ne' scadenze, e una refetch qui
 * farebbe solo sparire e riapparire la tabella sotto le mani di chi sta lavorando la coda.
 * La conferma la porta il toast, che distingue il sollecito su rata scaduta dal semplice
 * promemoria.
 */
export const useSendInstallmentReminder = () => {
    const { toast } = useToast();
    const { t } = useI18n();

    return useMutation({
        mutationFn: (params: { installmentId: string; language?: AppLanguage }) =>
            sendInstallmentReminder(params.installmentId, params.language),
        onSuccess: (result) => {
            toast({
                title:
                    result.template === 'installment_overdue'
                        ? t('payments.reminder.sentOverdue', { days: result.days_overdue })
                        : t('payments.reminder.sent'),
                description: result.to_email,
            });
        },
        onError: (error) => {
            toast({ title: t(paymentPlanErrorKey(error) as TranslationKey), variant: 'destructive' });
        },
    });
};

/** Mostra un toast quando la dashboard non si carica, senza esporre l'errore del database. */
export const usePaymentsDashboardError = (error: unknown) => {
    const { toast } = useToast();
    const { t } = useI18n();

    useEffect(() => {
        if (!error) return;
        toast({ title: t(paymentsDashboardErrorKey(error) as TranslationKey), variant: 'destructive' });
    }, [error, toast, t]);
};

// Il debounce non e' piu' specifico dei pagamenti: vive in @/hooks e viene
// ri-esportato qui perche' i consumatori esistenti continuino a importarlo da
// questo modulo.
export { useDebouncedValue } from '@/hooks/useDebouncedValue';
